'use client';

import Image from 'next/image';
import Link from 'next/link';
import type { Product } from '@/lib/shopify/types';
import { formatPrice } from '@/lib/shopify';

interface RelatedProductsProps {
  products: Product[];
  currentProductId: string;
}

export default function RelatedProducts({ products, currentProductId }: RelatedProductsProps) {
  // Filter out the current product and limit to 4
  const relatedProducts = products
    .filter((p) => p.id !== currentProductId)
    .slice(0, 4);

  if (relatedProducts.length === 0) return null;

  return (
    <section className="py-12 lg:py-16 border-t border-[var(--color-border)]">
      <div className="container">
        <h2 className="text-lg font-bold uppercase tracking-wider mb-8">
          You May Also Like
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {relatedProducts.map((product) => (
            <Link
              key={product.id}
              href={`/products/${product.handle}`}
              className="group"
            >
              <div className="relative aspect-[3/4] bg-[var(--color-off-white)] overflow-hidden mb-3">
                {product.featuredImage ? (
                  <Image
                    src={product.featuredImage.url}
                    alt={product.featuredImage.altText || product.title}
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                    No image
                  </div>
                )}
                {/* Wishlist Button */}
                <button
                  className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-colors opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.preventDefault();
                    // TODO: Add wishlist functionality
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                    />
                  </svg>
                </button>
              </div>
              <h3 className="text-sm font-medium text-[var(--color-text-primary)] line-clamp-1 group-hover:underline">
                {product.title}
              </h3>
              <p className="text-sm font-semibold text-[var(--color-text-primary)] mt-1">
                {formatPrice(product.priceRange.minVariantPrice)}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
